/**
 * «Собеседник печатает» для всех диалогов сразу.
 *
 * Кадры `typing` эфемерны и не попадают в реплику (BS §9.4), поэтому их
 * держит отдельный стор: индикатор нужен и в открытом диалоге, и в строке
 * списка чатов, как в Telegram. Кадр живёт 5 с; `stop` гасит его раньше.
 */
import { useEffect, useReducer } from 'react'
import { create } from 'zustand'

import type { TypingFrame } from '@shared/ws/socket'

/** BS §9.4: кадр typing действует 5 с. */
export const TYPING_TTL_MS = 5000

interface TypingState {
  /** conversation_id → момент, до которого собеседник «печатает». */
  until: Record<string, number>
  apply: (frame: TypingFrame, myUserId: string) => void
}

export const useTypingStore = create<TypingState>((set) => ({
  until: {},
  apply: (frame, myUserId) =>
    set((state) => {
      // Собственные кадры приходят эхом с других устройств — это не собеседник.
      if (frame.user_id === myUserId) return state
      const next = { ...state.until }
      if (frame.state === 'start') next[frame.conversation_id] = Date.now() + TYPING_TTL_MS
      else delete next[frame.conversation_id]
      return { until: next }
    }),
}))

/** Печатает ли собеседник в диалоге; гаснет по истечении кадра без нового `stop`. */
export function useTyping(conversationId: string | null): boolean {
  const until = useTypingStore((s) => (conversationId ? (s.until[conversationId] ?? 0) : 0))
  const [, tick] = useReducer((n: number) => n + 1, 0)

  useEffect(() => {
    const remaining = until - Date.now()
    if (remaining <= 0) return
    const timer = setTimeout(tick, remaining + 20)
    return () => clearTimeout(timer)
  }, [until])

  return until > Date.now()
}
